import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Search,
  X,
  LayoutDashboard,
  BookOpen,
  Hammer,
  Rocket,
  Activity,
  Server,
  DollarSign,
  BarChart3,
  Radio,
  Power,
  Users,
  ScrollText,
  Database,
  type LucideIcon,
} from 'lucide-react';
import { clsx } from 'clsx';
import { fetchNamespaces } from '@/api/namespaces';
import { fetchEC2Instances, fetchRDSInstances } from '@/api/awsResources';

export default function GlobalSearch() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const trimmed = query.trim().toLowerCase();
  const hasQuery = trimmed.length > 0;

  const namespacesQ = useQuery({
    queryKey: ['search', 'namespaces'],
    queryFn: () => fetchNamespaces(false),
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });
  const ec2Q = useQuery({
    queryKey: ['search', 'ec2'],
    queryFn: fetchEC2Instances,
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });
  const rdsQ = useQuery({
    queryKey: ['search', 'rds'],
    queryFn: fetchRDSInstances,
    enabled: open,
    staleTime: 60_000,
    retry: false,
  });

  const pageResults = hasQuery
    ? ALL_PAGES.filter((p) =>
        p.label.toLowerCase().includes(trimmed) ||
        p.keywords.some((k) => k.includes(trimmed)),
      )
    : [];

  const namespaceResults = hasQuery
    ? (namespacesQ.data ?? []).filter((n) => n.name.toLowerCase().includes(trimmed)).slice(0, 8)
    : [];

  const ec2Results = hasQuery
    ? (ec2Q.data ?? [])
        .filter((i) => {
          const name = (i.name ?? '').toLowerCase();
          const id = (i.instance_id ?? '').toLowerCase();
          return name.includes(trimmed) || id.includes(trimmed);
        })
        .slice(0, 6)
    : [];

  const rdsResults = hasQuery
    ? (rdsQ.data ?? []).filter((d) => (d.db_identifier ?? '').toLowerCase().includes(trimmed)).slice(0, 6)
    : [];

  const flatResults: SearchResult[] = [
    ...pageResults.map<SearchResult>((p) => ({ kind: 'page', label: p.label, sub: undefined, to: p.to, icon: p.icon })),
    ...namespaceResults.map<SearchResult>((n) => ({
      kind: 'namespace',
      label: n.name,
      sub: `${n.status} · ${n.total_workloads} workload${n.total_workloads === 1 ? '' : 's'}`,
      to: '/environments',
      icon: Power,
    })),
    ...ec2Results.map<SearchResult>((i) => ({
      kind: 'ec2',
      label: i.name || i.instance_id,
      sub: `${i.instance_id} · ${i.instance_type} · ${i.state}`,
      to: '/environments',
      icon: Server,
    })),
    ...rdsResults.map<SearchResult>((d) => ({
      kind: 'rds',
      label: d.db_identifier,
      sub: `${d.engine} · ${d.status}`,
      to: '/environments',
      icon: Database,
    })),
  ];

  const totalResults = flatResults.length;
  const isAnyResourceLoading = namespacesQ.isLoading || ec2Q.isLoading || rdsQ.isLoading;

  useEffect(() => { setSelectedIndex(0); }, [query]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  function navigateTo(result: SearchResult) {
    navigate(result.to);
    setQuery('');
    setOpen(false);
    inputRef.current?.blur();
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Escape') { setQuery(''); setOpen(false); inputRef.current?.blur(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex((i) => Math.min(i + 1, totalResults - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setSelectedIndex((i) => Math.max(i - 1, 0)); return; }
    if (e.key === 'Enter' && flatResults[selectedIndex]) navigateTo(flatResults[selectedIndex]);
  }

  return (
    <div className="relative w-full max-w-md" ref={containerRef}>
      <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={handleKeyDown}
        placeholder="Search pages, namespaces, resources…"
        className="w-full rounded-lg border border-slate-200 bg-surface-50 py-2 pl-10 pr-16 text-sm text-slate-800 placeholder-slate-400 transition-all duration-200 focus:border-brand-400 focus:bg-white focus:outline-none focus:ring-2 focus:ring-brand-500/15 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500 dark:focus:bg-slate-800"
      />

      {!query && (
        <div className="pointer-events-none absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
          <kbd className="rounded border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-400 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-500">
            ⌘K
          </kbd>
        </div>
      )}

      {query && (
        <button
          onClick={() => { setQuery(''); setOpen(false); }}
          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-700 dark:hover:text-slate-200"
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}

      {open && hasQuery && (
        <div className="animate-scale-in absolute left-0 right-0 top-full z-50 mt-2 max-h-[28rem] overflow-y-auto rounded-xl bg-white py-1 shadow-dropdown dark:bg-slate-800 dark:ring-1 dark:ring-slate-700">
          {totalResults === 0 && !isAnyResourceLoading && (
            <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">
              No results for &ldquo;<span className="font-medium text-slate-700 dark:text-slate-300">{query}</span>&rdquo;
            </div>
          )}

          {totalResults === 0 && isAnyResourceLoading && (
            <div className="px-4 py-8 text-center text-sm text-slate-500 dark:text-slate-400">Searching…</div>
          )}

          <SearchResultList results={flatResults} selectedIndex={selectedIndex} onHover={setSelectedIndex} onSelect={navigateTo} />
        </div>
      )}
    </div>
  );
}

function SearchResultList({
  results, selectedIndex, onHover, onSelect,
}: {
  results: SearchResult[];
  selectedIndex: number;
  onHover: (index: number) => void;
  onSelect: (r: SearchResult) => void;
}) {
  let lastKind: SearchResult['kind'] | null = null;
  return (
    <>
      {results.map((r, i) => {
        const header = r.kind !== lastKind ? <SectionHeader kind={r.kind} /> : null;
        lastKind = r.kind;
        const Icon = r.icon;
        return (
          <div key={`${r.kind}-${r.to}-${r.label}-${i}`}>
            {header}
            <button
              onClick={() => onSelect(r)}
              onMouseEnter={() => onHover(i)}
              className={clsx(
                'flex w-full items-center gap-3 px-4 py-2 text-left text-sm transition-colors duration-100',
                i === selectedIndex
                  ? 'bg-brand-50 text-brand-700 dark:bg-brand-950/40 dark:text-brand-300'
                  : 'text-slate-700 hover:bg-slate-50 dark:text-slate-300 dark:hover:bg-slate-700/40',
              )}
            >
              <Icon className={clsx('h-4 w-4 shrink-0', i === selectedIndex ? 'text-brand-500' : 'text-slate-400 dark:text-slate-500')} />
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{r.label}</p>
                {r.sub && <p className="truncate text-xs text-slate-400 dark:text-slate-500">{r.sub}</p>}
              </div>
            </button>
          </div>
        );
      })}
    </>
  );
}

function SectionHeader({ kind }: { kind: SearchResult['kind'] }) {
  const labels: Record<SearchResult['kind'], string> = { page: 'Pages', namespace: 'Namespaces', ec2: 'EC2 Instances', rds: 'RDS Databases' };
  return (
    <div className="border-t border-slate-100 px-4 pb-1 pt-2.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400 first:border-t-0 dark:border-slate-700/60 dark:text-slate-500">
      {labels[kind]}
    </div>
  );
}

interface SearchResult {
  kind: 'page' | 'namespace' | 'ec2' | 'rds';
  label: string;
  sub?: string;
  to: string;
  icon: LucideIcon;
}

const ALL_PAGES: { to: string; label: string; icon: LucideIcon; keywords: string[] }[] = [
  { to: '/', label: 'Dashboard', icon: LayoutDashboard, keywords: ['home', 'overview', 'metrics'] },
  { to: '/services', label: 'Service Catalog', icon: BookOpen, keywords: ['services', 'catalog', 'api'] },
  { to: '/builds', label: 'Builds', icon: Hammer, keywords: ['ci', 'pipeline', 'build'] },
  { to: '/deployments', label: 'Deployments', icon: Rocket, keywords: ['deploy', 'release', 'rollout'] },
  { to: '/observability', label: 'Observability', icon: Activity, keywords: ['logs', 'metrics', 'tracing', 'monitoring'] },
  { to: '/infrastructure', label: 'Infrastructure', icon: Server, keywords: ['infra', 'clusters', 'nodes', 'kubernetes', 'k8s'] },
  { to: '/costs', label: 'Cost Analytics', icon: DollarSign, keywords: ['costs', 'billing', 'spend', 'budget'] },
  { to: '/management', label: 'Management Dashboard', icon: BarChart3, keywords: ['management', 'admin', 'reports'] },
  { to: '/environments', label: 'Environments', icon: Power, keywords: ['namespaces', 'start', 'stop', 'eks', 'env', 'ec2', 'rds'] },
  { to: '/admin/users', label: 'Users', icon: Users, keywords: ['users', 'admin', 'team', 'people', 'accounts', 'invite'] },
  { to: '/admin/audit-logs', label: 'Audit logs', icon: ScrollText, keywords: ['audit', 'logs', 'security', 'activity', 'history'] },
  { to: '/collectors', label: 'Collector Status', icon: Radio, keywords: ['collector', 'otel', 'opentelemetry'] },
];
